import React, { useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { photosWithDuplicatesShape } from './constants';
import screenAndStyle from '../../../_utils/screen-and-style';
import { DuplicatePhotoRow } from './duplicate-photo-row';
import { useDuplicatePhotoSelection } from './use-duplicate-photo-selection';
import { ConfirmationModal } from './confirmation-modal';
import { usePrintsOrderStore } from '../../stores/use-prints-order-store';
import { usePrintsConfigStore } from '../../stores/use-prints-config-store';
import { selectTotalQuantity } from '../../stores/selectors';
import sendToGoogleAnalytics from '../../../_libs/analytics/builder-send-to-google-analytics';

/**
 * @param {object} props
 * @param {boolean} props.disabled - Whether prints builder updating is currently enabled or not.
 * @param {array} props.photosWithDuplicates - Results from duplicate detection consisting of original photo and any duplicates.
 */
const DuplicatePhotosList = ({
  disabled = false,
  photosWithDuplicates = [],
  totalDuplicatesCount = 0,
}) => {
  const allSizes = usePrintsConfigStore((state) => state.allSizes);
  const photos = usePrintsOrderStore((state) => state.photos);
  const removePhotoFromSize = usePrintsOrderStore(
    (state) => state.removePhotoFromSize,
  );
  const {
    selectedPhotos,
    allSelected,
    handlePhotoSelection,
    handleBulkAdd,
    handleBulkRemove,
    handleSelectAllDuplicates,
  } = useDuplicatePhotoSelection(photosWithDuplicates);
  const confirmationModalRef = useRef(null);

  // build list of photos with `size` object for view, indexed by `image_id`
  const sizeByKey = Object.fromEntries(allSizes.map((s) => [s.key, s]));
  const photosById = photos.reduce((acc, photo) => {
    acc[photo.image_id] = { ...photo, size: sizeByKey[photo.sizeKey] };
    return acc;
  }, {});

  const handleRemoveSelectedPhotos = () => {
    sendToGoogleAnalytics('duplicate_photos_removed', '', {
      total_photos_count: selectTotalQuantity(photos), // eslint-disable-line camelcase
      removed_duplicates_count: selectedPhotos.size, // eslint-disable-line camelcase
      total_duplicates_count: totalDuplicatesCount, // eslint-disable-line camelcase
    });

    const clientUuids = [...selectedPhotos]
      .map((imageId) => photosById[imageId]?.client_uuid)
      .filter(Boolean);

    removePhotoFromSize({ clientUuids });
  };

  const openConfirmationModal = () => {
    if (confirmationModalRef.current) {
      confirmationModalRef.current.open();
    }
  };

  if (!photosWithDuplicates.length) {
    return null;
  }

  return (
    <div
      className={classNames('duplicate-photos__view', {
        touch: screenAndStyle.isTouch(),
      })}>
      <div className="w-100 d-flex justify-content-between align-items-center grey-zone duplicate-photos__originals-count border-top p-2">
        <span>
          {photosWithDuplicates.length === 1
            ? '1 of your photos has had multiple copies uploaded.'
            : `${photosWithDuplicates.length} of your photos have had multiple copies uploaded.`}{' '}
          Please review and remove as required.
        </span>
        <div className="d-flex flex-shrink-0">
          <button
            type="button"
            className="btn btn-secondary rounded-pill d-none d-md-block"
            onClick={handleSelectAllDuplicates}
            disabled={allSelected || disabled}>
            Select All Duplicates
          </button>
          <button
            type="button"
            className="btn btn-primary rounded-pill ml-md-2"
            onClick={openConfirmationModal}
            disabled={selectedPhotos.size === 0 || disabled}>
            Remove All Selected
          </button>
        </div>
      </div>

      <div className="list-view list-view-desktop duplicate-photos__list">
        {photosWithDuplicates.map((duplicatePhoto) => {
          const id = duplicatePhoto.imageId;
          const originalPhoto = photosById[id];
          const duplicatePhotos = duplicatePhoto.duplicateImageIds
            .map((imageId) => photosById[imageId])
            .filter(Boolean);

          return originalPhoto && !!duplicatePhotos.length ? (
            <DuplicatePhotoRow
              key={`${duplicatePhoto.key}--${id}`}
              originalPhoto={originalPhoto}
              duplicatePhotos={duplicatePhotos}
              disabled={disabled}
              sizeKey={duplicatePhoto.key}
              selectedPhotos={selectedPhotos}
              onPhotoSelect={handlePhotoSelection}
              onBulkSelect={handleBulkAdd}
              onBulkDeselect={handleBulkRemove}
            />
          ) : null;
        })}
        <ConfirmationModal
          ref={confirmationModalRef}
          onConfirm={handleRemoveSelectedPhotos}
          selectedCount={selectedPhotos.size}
        />
      </div>
    </div>
  );
};

DuplicatePhotosList.propTypes = {
  disabled: PropTypes.bool,
  photosWithDuplicates: PropTypes.arrayOf(
    PropTypes.shape(photosWithDuplicatesShape),
  ),
  totalDuplicatesCount: PropTypes.number,
};

export { DuplicatePhotosList };
